import { call, fork, put, select } from 'redux-saga/effects';
import { Action } from '@reduxjs/toolkit';
import { UserEntries, UserId, UserLeftPayload, UsersState } from 'features/users/types';
import {
  selectActiveUsers,
  selectEvictableAwaitingEntryIds,
  selectUserEntries,
} from 'features/users/usersSlice';
import { selectIsSystemSession } from 'features/application/selectors';
import { cleanupUsers } from 'features/users/thunks/cleanupUsers';
import { RTCClient } from 'utils/webrtc';
import { selectE2eeEnabled } from 'features/e2ee/e2eeSlice';
import { cleanupE2eeUser } from 'features/e2ee/cleanupE2eeUser';
import { refreshEncryptionKeys } from 'features/e2ee/sagas/refreshEncryptionKeys';
import { board } from 'utils/whiteboard/BoardStateManager';
import {
  raiseHandNotificationUpdateRequested,
  userLeftNotificationTriggered,
} from 'features/notifications/actions';
import { eventBus } from 'utils/eventBus';
import { refineSframeStatus } from 'features/e2ee/sagas/refineSframeStatus';
import { signalingUserLeftBatch, signalingWaitingUserLeftBatch } from 'features/users/actions';
import { selectMainRoomId } from 'features/breakout-rooms/breakoutRoomsSlice';
import { cleanupUserScreensharesSaga } from 'features/screenshare/sagas/cleanupUserScreensharesSaga';
import { selectScreenshareStreams } from 'features/layout/features/order/orderSlice';
import { ScreenshareSourceDetails } from 'features/layout/types';

export function* onUserLeftBatchedSaga(action: ReturnType<typeof signalingUserLeftBatch>) {
  const { users } = action.payload;

  const isSystemSession: boolean = yield select(selectIsSystemSession);
  const e2eeEnabled: boolean = yield select(selectE2eeEnabled);

  const userEntities: UserEntries = yield select(selectUserEntries);
  const activeUsers: UsersState['activeUsers'] = yield select(selectActiveUsers);
  const evictableAwaitingEntryIds: UserId[] = yield select(selectEvictableAwaitingEntryIds);
  const availableUserIds: UserLeftPayload[] = [];
  const leavingUserIds = new Set<string>();

  // waiting ids that never entered the room: the server has already removed them from its
  // waiting collection, and this userLeft is the only signal of that removal
  const staleWaitingUsers = users.filter(
    ({ id }) => !activeUsers[id] && evictableAwaitingEntryIds.includes(id)
  );
  if (staleWaitingUsers.length > 0) {
    yield put(signalingWaitingUserLeftBatch({ users: staleWaitingUsers }));
  }

  if (e2eeEnabled) {
    yield call(refineSframeStatus);
  }

  const mainRoomId: string = yield select(selectMainRoomId);

  // filter out users not available to prevent possible race-condition from the backend
  for (const { id, roomId: payloadRoomId } of users) {
    // called directly rather than through `call`: these are synchronous side effects,
    // and one effect per leaver buys nothing on a batch this size
    const user = activeUsers[id] ? userEntities[id] : undefined;
    if (user) {
      const roomId = payloadRoomId || mainRoomId;
      availableUserIds.push({ userId: user.id, roomId });
      leavingUserIds.add(user.id);

      // cleanup E2EE stuff
      if (e2eeEnabled) {
        cleanupE2eeUser(user.id, roomId, user.e2eeId!);
      }

      RTCClient.receivingFeed.unloadFeedAudio(user.id);
      board.removeRemotePointer(user.id);
    }
  }

  if (availableUserIds.length === 0) {
    return;
  }

  const screenshares: ScreenshareSourceDetails[] = yield select(selectScreenshareStreams);
  const screensharingLeaverIds = new Set(
    screenshares.filter((s) => leavingUserIds.has(s.userId)).map((s) => s.userId)
  );

  for (const userId of screensharingLeaverIds) {
    yield call(cleanupUserScreensharesSaga, userId);
  }

  if (!isSystemSession) {
    yield put(userLeftNotificationTriggered(availableUserIds));

    yield call(eventBus.sendMessage, 'userLeftBatch', undefined, {
      reason: 'generic',
      userIds: availableUserIds,
    });
  }

  const cleanupUsersThunk: Action = yield call(cleanupUsers, availableUserIds);
  yield put(cleanupUsersThunk);

  if (!isSystemSession) {
    // we have to wait until the user is completely removed from the store
    if (e2eeEnabled) {
      yield fork(refreshEncryptionKeys);
    }

    // update raise hand notification
    yield put(raiseHandNotificationUpdateRequested({ reason: 'user-left' }));
  }
}
